'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowRight, ClipboardList, ScanSearch, Wallet, Zap, ShieldCheck, CheckCircle2, Sparkles } from 'lucide-react';
import ScrollAnimation from './ScrollAnimation';

const STEPS = [
  {
    step: 1,
    title: 'Enter Your Travel Details',
    subtitle: 'STEP 01',
    description:
      'Provide your passenger information, intended departure and arrival locations, travel dates, and other requested itinerary details.',
    icon: ClipboardList,
    side: 'left',
    threshold: 0.05,
  },
  {
    step: 2,
    title: 'Choose Your Flight Itinerary',
    subtitle: 'STEP 02',
    description:
      'Select the itinerary option that matches your intended travel plans, such as a one-way, return, or multi-city journey.',
    icon: ScanSearch,
    side: 'right',
    threshold: 0.28,
  },
  {
    step: 3,
    title: 'Review Your Information',
    subtitle: 'STEP 03',
    description:
      'Check the passenger name, route, dates, and flight details carefully before completing your request.',
    icon: Wallet,
    side: 'left',
    threshold: 0.55,
  },
  {
    step: 4,
    title: 'Receive Your Ticket',
    subtitle: 'STEP 04',
    description:
      'Your flight itinerary or reservation document is prepared according to the selected service and delivery option.',
    icon: Zap,
    side: 'right',
    threshold: 0.78,
  },
];

const CALM_GRADIENTS = {
  1: {
    // Step 1: Soft Sky Blue & Soft Mint Cyan
    bg: 'bg-gradient-to-br from-[#e0f2fe] via-[#f0f9ff] to-[#e0f7fa]',
    border: 'border-sky-300/80',
    shadow: 'shadow-[0_12px_30px_rgba(56,189,248,0.22)] scale-[1.02]',
    arrowLeft: 'after:border-l-[#e0f7fa]',
    arrowRight: 'before:border-r-[#e0f7fa]',
    nodeRing: 'ring-sky-400 bg-sky-600 scale-110 shadow-lg shadow-sky-300/50',
    badge: 'bg-sky-600 text-white shadow-sm',
    iconBg: 'bg-sky-600 text-white shadow-md shadow-sky-200',
  },
  2: {
    // Step 2: Soft Calm Indigo & Periwinkle
    bg: 'bg-gradient-to-br from-[#eef2ff] via-[#f5f3ff] to-[#e0f2fe]',
    border: 'border-indigo-300/80',
    shadow: 'shadow-[0_12px_30px_rgba(129,140,248,0.22)] scale-[1.02]',
    arrowLeft: 'after:border-l-[#e0f2fe]',
    arrowRight: 'before:border-r-[#eef2ff]',
    nodeRing: 'ring-indigo-400 bg-indigo-600 scale-110 shadow-lg shadow-indigo-300/50',
    badge: 'bg-indigo-600 text-white shadow-sm',
    iconBg: 'bg-indigo-600 text-white shadow-md shadow-indigo-200',
  },
  3: {
    // Step 3: Soft Calm Teal & Emerald Mist
    bg: 'bg-gradient-to-br from-[#e6fffa] via-[#ecfdf5] to-[#e0f2fe]',
    border: 'border-teal-300/80',
    shadow: 'shadow-[0_12px_30px_rgba(45,212,191,0.22)] scale-[1.02]',
    arrowLeft: 'after:border-l-[#e6fffa]',
    arrowRight: 'before:border-r-[#e6fffa]',
    nodeRing: 'ring-teal-400 bg-teal-600 scale-110 shadow-lg shadow-teal-300/50',
    badge: 'bg-teal-600 text-white shadow-sm',
    iconBg: 'bg-teal-600 text-white shadow-md shadow-teal-200',
  },
  4: {
    // Step 4: Soft Warm Amber & Azure
    bg: 'bg-gradient-to-br from-[#fff7ed] via-[#fef3c7]/60 to-[#e0f2fe]',
    border: 'border-amber-300/80',
    shadow: 'shadow-[0_12px_30px_rgba(251,191,36,0.25)] scale-[1.02]',
    arrowLeft: 'after:border-l-[#e0f2fe]',
    arrowRight: 'before:border-r-[#fff7ed]',
    nodeRing: 'ring-amber-400 bg-amber-600 scale-110 shadow-lg shadow-amber-300/50',
    badge: 'bg-amber-600 text-white shadow-sm',
    iconBg: 'bg-amber-600 text-white shadow-md shadow-amber-200',
  },
};

function TopDownAirplane({ isScrollingUp = false }) {
  return (
    <div className="relative flex items-center justify-center pointer-events-none select-none">
      {/* Soft Drop Shadow cast onto runway */}
      <svg
        viewBox="0 0 120 140"
        className="absolute left-4 top-4 w-16 md:w-20 opacity-20 filter blur-sm"
        fill="#0c2635"
      >
        <path d="M60 4 C51 4 47 28 47 68 C47 114 51 136 60 136 C69 136 73 114 73 68 C73 28 69 4 60 4 Z" />
        <path d="M60 50 L2 80 L2 92 L56 72 Z" />
        <path d="M60 50 L118 80 L118 92 L64 72 Z" />
      </svg>

      {/* Main 2D Top-Down Aircraft Vector */}
      <svg
        viewBox="0 0 120 140"
        className={`w-16 md:w-20 drop-shadow-xl relative z-20 transition-transform duration-500 ${isScrollingUp ? 'rotate-0' : 'rotate-180'}`}
      >
        <defs>
          <linearGradient id="fuselage-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f8fafc" />
            <stop offset="45%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#cbd5e1" />
          </linearGradient>
          <linearGradient id="wing-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
          <linearGradient id="accent-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#156482" />
            <stop offset="100%" stopColor="#38bdf8" />
          </linearGradient>
        </defs>

        {/* Main Wings */}
        <path
          d="M60 50 L2 80 L2 92 L56 72 Z"
          fill="url(#wing-grad)"
          stroke="#94a3b8"
          strokeWidth="0.5"
        />
        <path
          d="M60 50 L118 80 L118 92 L64 72 Z"
          fill="url(#wing-grad)"
          stroke="#94a3b8"
          strokeWidth="0.5"
        />

        {/* Tail Wings */}
        <path d="M60 112 L22 130 L22 136 L58 124 Z" fill="url(#wing-grad)" />
        <path d="M60 112 L98 130 L98 136 L62 124 Z" fill="url(#wing-grad)" />

        {/* Jet Engines */}
        <rect x="26" y="74" width="7" height="18" rx="3.5" fill="#334155" />
        <rect x="40" y="68" width="7" height="18" rx="3.5" fill="#334155" />
        <rect x="73" y="68" width="7" height="18" rx="3.5" fill="#334155" />
        <rect x="87" y="74" width="7" height="18" rx="3.5" fill="#334155" />

        {/* Fuselage (Body) */}
        <path
          d="M60 136 C51 136 47 114 47 68 C47 28 51 4 60 4 C69 4 73 28 73 68 C73 114 69 136 60 136 Z"
          fill="url(#fuselage-grad)"
          stroke="#64748b"
          strokeWidth="0.75"
        />

        {/* Nose Cone Accent */}
        <path
          d="M60 4 C64 4 68 12 68 20 C68 20 60 18 60 18 C60 18 52 20 52 20 C52 12 56 4 60 4 Z"
          fill="url(#accent-grad)"
        />

        {/* Cockpit Windows */}
        <path
          d="M53 22 C53 22 57 17 60 17 C63 17 67 22 67 22 C65 20 62 19 60 19 C58 19 55 20 53 22 Z"
          fill="#0f172a"
        />

        {/* Wingtip Lights */}
        <circle cx="3" cy="81" r="2" fill="#ef4444" />
        <circle cx="117" cy="81" r="2" fill="#22c55e" />
      </svg>
    </div>
  );
}

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    let animationFrameId;
    let ticking = false;
    let isIntersecting = false;

    const calculateScroll = () => {
      if (!trackRef.current) return;
      
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY.current - 2) {
        setIsScrollingUp(true);
      } else if (currentScrollY > lastScrollY.current + 2) {
        setIsScrollingUp(false);
      }
      lastScrollY.current = currentScrollY;

      // Instead of getBoundingClientRect() on every frame, use cached or offset calculations
      // to avoid forced reflows/layout thrashing.
      const trackElement = trackRef.current;
      const rectTop = trackElement.getBoundingClientRect().top; // we still need it relative to viewport, but let's just get it once per tick, or even better, calculate from absolute document position!
      const totalDistance = trackElement.offsetHeight;
      const windowHeight = window.innerHeight;

      const startPoint = windowHeight * 0.7;
      const currentScroll = startPoint - rectTop;

      let progress = currentScroll / totalDistance;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      setScrollProgress(progress);
    };

    const handleScroll = () => {
      if (!ticking && isIntersecting) {
        animationFrameId = requestAnimationFrame(() => {
          calculateScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry.isIntersecting;
        if (isIntersecting) {
          window.addEventListener('scroll', handleScroll, { passive: true });
          calculateScroll();
        } else {
          window.removeEventListener('scroll', handleScroll);
        }
      },
      { rootMargin: '200px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative w-full overflow-hidden py-14 md:py-20 bg-gradient-to-b from-slate-50/70 via-[#e0f2fe]/30 to-slate-50/70"
      aria-labelledby="how-it-works-heading"
    >
      {/* Background Glows */}
      <div className="pointer-events-none absolute left-0 top-24 h-[350px] w-[350px] -translate-x-1/2 rounded-full bg-primary/10 blur-[100px]" />
      <div className="pointer-events-none absolute bottom-12 right-0 h-[300px] w-[300px] translate-x-1/3 rounded-full bg-secondary/10 blur-[90px]" />

      <div className="custom-mx relative z-10 mx-auto max-w-6xl">
        {/* Section Header */}
        <div className="mx-auto mb-14 max-w-3xl text-center md:mb-20">
          <p className="mb-3 text-[11px] font-bold tracking-widest text-primary">Simple &amp; Fast Process</p>
          <h2
            id="how-it-works-heading"
            className="text-2xl font-extrabold tracking-tight text-slate-850 md:text-3xl lg:text-4xl"
          >
            How Our Visa Flight Itinerary Service Works
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-500 md:text-lg">
            The process is intentionally simple: provide details, confirm accuracy, then receive your document. Designed
            to reduce mistakes when timelines are tight.
          </p>
        </div>

        {/* Runway Timeline Container */}
        <div ref={trackRef} className="relative mx-auto my-6 max-w-5xl">
          {/* Vertical Runway Track Line (Desktop Center, Mobile Left) */}
          <div className="absolute top-0 bottom-0 left-6 md:left-1/2 -translate-x-1/2 w-14 md:w-20 rounded-full bg-gradient-to-b from-[#cbecf7] via-[#b9e6f6] to-[#cbecf7] border border-sky-200/80 shadow-inner flex justify-center z-0 overflow-hidden">
            {/* Center Dashed Runway Markings */}
            <div className="h-full w-0.5 border-r-2 border-dashed border-primary/40" />
          </div>

          <div
            className="absolute left-6 md:left-1/2 -translate-x-1/2 z-20 transition-all duration-300 ease-out"
            style={{
              top: `${scrollProgress * 84}%`,
            }}
          >
            <TopDownAirplane isScrollingUp={isScrollingUp} />
          </div>

          {/* Steps Callout Speech Bubbles */}
          <div className="relative z-10 space-y-12 md:space-y-20 py-8">
            {STEPS.map((item, index) => {
              const Icon = item.icon;
              const isLeft = item.side === 'left';
              const isReached = scrollProgress >= item.threshold;
              const calmStyle = CALM_GRADIENTS[item.step];

              return (
                <div key={item.step} className="relative flex items-center md:justify-between">
                  {/* Desktop Left Callout Bubble */}
                  <div className="hidden md:flex md:w-1/2 md:pr-12 md:justify-end">
                    {isLeft ? (
                      <ScrollAnimation delay={index * 0.1}>
                        <div
                          className={`group relative w-full max-w-md rounded-2xl border p-6 md:p-7 transition-all duration-500 ease-out after:absolute after:top-1/2 after:-right-3 after:-translate-y-1/2 after:border-y-8 after:border-y-transparent after:border-l-[12px] ${
                            isReached
                              ? `${calmStyle.bg} ${calmStyle.border} ${calmStyle.shadow} ${calmStyle.arrowLeft}`
                              : 'bg-white border-slate-200/90 shadow-md after:border-l-white'
                          }`}
                        >
                          <div className="mb-3 flex items-center justify-end gap-2.5">
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-widest transition-colors duration-500 ${
                                isReached ? calmStyle.badge : 'text-primary bg-primary/10'
                              }`}
                            >
                              {item.subtitle}
                            </span>
                            <div
                              className={`flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-500 ${
                                isReached ? calmStyle.iconBg : 'bg-primary/10 text-primary'
                              }`}
                            >
                              <Icon className="h-5 w-5" strokeWidth={2} />
                            </div>
                          </div>
                          <h3 className="text-right text-lg font-extrabold text-slate-850">{item.title}</h3>
                          <p className="mt-2 text-right text-xs leading-relaxed text-slate-600 md:text-sm">
                            {item.description}
                          </p>
                        </div>
                      </ScrollAnimation>
                    ) : (
                      <div className="w-full" />
                    )}
                  </div>

                  {/* Central Runway Circle Node */}
                  <div
                    className={`absolute left-6 md:relative md:left-0 -translate-x-1/2 z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-4 text-white font-black text-sm md:h-14 md:w-14 md:text-base transition-all duration-500 ${
                      isReached
                        ? calmStyle.nodeRing
                        : 'bg-[#0c2635] ring-white shadow-lg'
                    }`}
                  >
                    0{item.step}
                  </div>

                  {/* Right Callout Bubble (Desktop Right & Mobile All) */}
                  <div className="w-full pl-16 md:pl-12 md:w-1/2 md:flex md:justify-start">
                    {(!isLeft || true) && (
                      <div className={`w-full ${isLeft ? 'md:hidden' : 'block'}`}>
                        <ScrollAnimation delay={index * 0.1}>
                          <div
                            className={`group relative w-full max-w-md rounded-2xl border p-6 md:p-7 transition-all duration-500 ease-out before:absolute before:top-1/2 before:-left-3 before:-translate-y-1/2 before:border-y-8 before:border-y-transparent before:border-r-[12px] ${
                              isReached
                                ? `${calmStyle.bg} ${calmStyle.border} ${calmStyle.shadow} ${calmStyle.arrowRight}`
                                : 'bg-white border-slate-200/90 shadow-md before:border-r-white'
                            }`}
                          >
                            <div className="mb-3 flex items-center justify-start gap-2.5">
                              <div
                                className={`flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-500 ${
                                  isReached ? calmStyle.iconBg : 'bg-primary/10 text-primary'
                                }`}
                              >
                                <Icon className="h-5 w-5" strokeWidth={2} />
                              </div>
                              <span
                                className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-widest transition-colors duration-500 ${
                                  isReached ? calmStyle.badge : 'text-primary bg-primary/10'
                                }`}
                              >
                                {item.subtitle}
                              </span>
                            </div>
                            <h3 className="text-left text-lg font-extrabold text-slate-850">{item.title}</h3>
                            <p className="mt-2 text-left text-xs leading-relaxed text-slate-600 md:text-sm">
                              {item.description}
                            </p>
                          </div>
                        </ScrollAnimation>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Applicant Tip / Important Reminder */}
        <div className="mx-auto mt-16 max-w-3xl text-left">
          <div className="relative overflow-hidden rounded-3xl border border-sky-200/80 bg-gradient-to-br from-white via-sky-50/60 to-white p-6 shadow-lg shadow-sky-100/60 md:p-8">
            <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-primary/10 blur-2xl" />

            <div className="relative z-10 mb-5 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <span className="text-[11px] font-bold tracking-widest text-primary">
                Important Applicant Tip
              </span>
            </div>

            <div className="relative z-10 grid gap-4 sm:grid-cols-2">
              {/* Tip 1 */}
              <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-white/90 p-4 shadow-sm backdrop-blur-sm transition-all hover:border-emerald-200 hover:shadow-md">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-800 md:text-sm">Verify Details First</h4>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Double-check passenger names and dates to avoid embassy submission delays.
                  </p>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-white/90 p-4 shadow-sm backdrop-blur-sm transition-all hover:border-sky-200 hover:shadow-md">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-primary">
                  <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-800 md:text-sm">100% Embassy Ready</h4>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Standard GDS format designed to meet consulate itinerary requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action CTA */}
        <div className="mt-12 flex justify-center">
          <Link
            href="/book"
            prefetch={false}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gradient-to-r from-primary to-secondary px-8 py-3.5 text-sm font-black text-white shadow-[0_10px_30px_rgba(85,178,213,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(85,178,213,0.4)]"
          >
            Continue to booking
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
